import * as d3 from 'd3'
import { useEffect, useRef } from 'react';
import useContainerDimensions from '../Graphs/useContainerDimensions'

// Tipos de datos correctos para TypeScript
type DataItem = {
  name: string;
  value: number;
};

type HistogramProps= {
  data: DataItem[],
  title: string;
}

const Histogram = ({data, title}:HistogramProps) =>{
  const svRef = useRef<SVGSVGElement>(null);
  //Se obtiene la referencia y las dimensiones
  const {ref: containerRef, dimensions} = useContainerDimensions();

  const width = dimensions.width;
  const height = dimensions.height;

  useEffect(() =>{
    // Asegura de que los datos no estén vacíos
    if (!width || !height || !data || data.length === 0) {
        d3.select(svRef.current).selectAll('*').remove();
        return;
    }

    // declare margins
    const margin = {top: 80, right: 60, bottom: 120, left: 60};
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    // 1. LIMPIEZA CORRECTA: Limpia el SVG referenciado directamente
    const svgElement = d3.select(svRef.current)
        .attr("width", width)
        .attr("height", height);
    
    svgElement.selectAll('*').remove(); // Limpia antes de dibujar

    // 2. CREACIÓN DEL GRUPO PRINCIPAL: El grupo donde dibujamos el gráfico
    const g = svgElement.append('g')
        // Mueve el grupo de dibujo al área definida por los márgenes
        .attr("transform", `translate(${margin.left}, ${margin.top})`); 

    // create the x axis scale, scaled to the states
    const xScale = d3.scaleBand()
        .domain(data.map(d => d.name))
        // Ahora usamos chartWidth para el rango
        .rangeRound([0, chartWidth]) 
        .padding(0.1)

    // create the y axis scale, scaled from 0 to the max
    const yScale = d3.scaleLinear()
        .domain([0, d3.max(data, d => d.value) as number])
        // Ahora usamos chartHeight para el rango
        .range([chartHeight, 0]) 

    // create a scale between colors that varies by the frequency
    const barColors = d3.scaleLinear<string>()
        .domain([0, d3.max(data, d => d.value) as number])
        .range(["blue", "red"]); // Uso de genéricos para TypeScript

    // 3. EJE X: set the x axis on the bottom.
    g.append("g")
        .attr('transform', `translate(0,${chartHeight})`)
        .call(d3.axisBottom(xScale))
        .selectAll("text")
        .style("text-anchor", "end")
        .attr("dx", "-.8em")
        .attr("dy", ".15em")
        .attr("transform", "rotate(-65)");

    // 4. EJE Y: set the y axis on the left
    g.append("g")
        .call(d3.axisLeft(yScale));
        // Etiqueta del Eje Y
    g.append("text")
        .attr("transform", "rotate(-90)")
        .attr("y", -margin.left + 15) // Lo movemos a la izquierda del área de dibujo
        .attr("x", -(chartHeight / 2))
        .style("text-anchor", "middle")
        .text("Cantidad elementos");
        
    // Título del Gráfico
    g.append("text")
        .attr("x", chartWidth / 2)
        .attr("y", -margin.top / 2 + 20)
        .attr("text-anchor", "middle")
        .style("font-size", "16px")
        .text(title);

    // 5. BARRAS: create the actual bars on the graph
    g.selectAll("rect")
        .data(data)
        .enter().append("rect")
        .attr('x', d => xScale(d.name)!)
        .attr('y', d => yScale(d.value))
        .attr('width', xScale.bandwidth())
        // La altura es la diferencia entre el punto cero (en el fondo) y el valor
        .attr('height', d => chartHeight - yScale(d.value)) 
        // ERROR CORREGIDO: Los estilos padding/margin/width no son para <rect>
        .attr("fill", d => barColors(d.value))
        .attr("stroke", "black")
        .attr("stroke-width", 1);
        
  },[data, height, width]) // Dependencias correctas
  
  // Renderiza el SVG principal, el useEffect lo poblará
  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', minHeight: '300px' }}>
      <svg ref={svRef} width={width} height={height}>
      </svg>
    </div>
  )
}
export default Histogram